import { useState, useEffect, useRef } from "react";
import emailjs from "@emailjs/browser";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./form.css";
import { useLanguage } from "../../i18n/LanguageContext";

gsap.registerPlugin(ScrollTrigger);

// Component untuk form slide
function FormSlide({ children, className = "", slideRef }) {
  return (
    <section className={`form-slide ${className}`} ref={slideRef}>
      {children}
    </section>
  );
}

export default function Form() {
  const { tr } = useLanguage();
  const nameInputRef = useRef(null);
  const formSectionRef = useRef(null);
  const slide1Ref = useRef(null);
  const slide3Ref = useRef(null);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    projectDetails: "",
    solutions: [], // multiple selection
    packages: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const solutionOptions = [
    { value: "Content Creator", label: tr("Content Creator", "Content Creator") },
    { value: "Online Business Owner", label: tr("Online Business Owner", "Online Business Owner") },
    { value: "Professional Service Provider", label: tr("Professional Service Provider", "Professional Service Provider") },
  ];

  const packageOptions = [
    { value: "Prototype", label: tr("Prototype", "Prototype") },
    { value: "Custom AI Solution", label: tr("Custom AI Solution", "Solusi AI Kustom") },
    { value: "Not Sure Yet", label: tr("Not Sure Yet", "Belum Yakin") },
  ];

  // EmailJS Configuration
  const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || "service_3anduw9";
  const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "template_zpiq81y";
  const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "EeoCxVL2v5wIIhpS6";

  useEffect(() => {
    // Initialize EmailJS with public key
    if (EMAILJS_PUBLIC_KEY) {
      emailjs.init(EMAILJS_PUBLIC_KEY);
    }
  }, []);

  // GSAP Slide Animation Effect (PPT Style)
  useEffect(() => {
    const ctx = gsap.context(() => {
      const slide1 = slide1Ref.current;
      const slide3 = slide3Ref.current;
      if (!slide1 || !slide3) return;

      const getSlideHeight = (el) => Math.max(window.innerHeight, el.offsetHeight);

      // Get all content elements for fade animation (only fade content, not background)
      // Create wrapper divs for grouping content if needed, or use direct children
      const slide1Content = slide1.querySelector(".form-heading-banner") || slide1;
      const slide1FormGroup = slide1.querySelector(".form-group");
      const slide3Content = slide3.querySelectorAll(".form-group, .form-submit-container");

      // Set initial state
      gsap.set(slide1, { autoAlpha: 1, y: 0 });
      gsap.set(slide3, { autoAlpha: 1, y: 0 });
      
      if (slide1Content) {
        gsap.set(slide1Content, { opacity: 1, y: 0 });
      }
      if (slide1FormGroup) {
        gsap.set(slide1FormGroup, { opacity: 1, y: 0 });
      }
      if (slide3Content.length > 0) {
        slide3Content.forEach((el) => {
          gsap.set(el, { opacity: 1, y: 0 });
        });
      }

      // Use pinning for both mobile and desktop
      // === SLIDE 1 ANIMATION ===
      // Pin section for full viewport height (like PowerPoint)
      ScrollTrigger.create({
        trigger: slide1,
        start: () => "top top",
        end: () => `+=${getSlideHeight(slide1)}`,
        pin: slide1,
        pinSpacing: true,
        scrub: false,
        invalidateOnRefresh: true,
      });

      // Fade in animation when section enters viewport (support scroll up)
      // Only fade content, not background
      if (slide1Content) {
        gsap.fromTo(
          slide1Content,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            ease: "power2.out",
            scrollTrigger: {
              trigger: slide1,
              start: () => "top 80%",
              end: () => "top 20%",
              scrub: true,
              invalidateOnRefresh: true,
            },
          }
        );
      }
      if (slide1FormGroup) {
      gsap.fromTo(
          slide1FormGroup,
          { opacity: 0, y: 30 },
        {
            opacity: 1,
          y: 0,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: slide1,
              start: () => "top 80%",
              end: () => "top 20%",
            scrub: true,
            invalidateOnRefresh: true,
          },
        }
      );
      }

      // Fade out animation when section exits (support scroll up)
      // Only fade content, keep background visible to prevent black screen
      if (slide1Content) {
        gsap.fromTo(
          slide1Content,
          { opacity: 1, y: 0 },
          {
            opacity: 0,
            y: -30,
            duration: 1,
            ease: "power2.in",
            scrollTrigger: {
              trigger: slide1,
              start: () => "bottom top",
              end: () => `bottom+=${getSlideHeight(slide1) * 0.5} top`,
              scrub: true,
              invalidateOnRefresh: true,
            },
          }
        );
      }
      if (slide1FormGroup) {
      gsap.fromTo(
          slide1FormGroup,
          { opacity: 1, y: 0 },
        {
            opacity: 0,
          y: -30,
          duration: 1,
          ease: "power2.in",
          scrollTrigger: {
            trigger: slide1,
              start: () => "bottom top",
              end: () => `bottom+=${getSlideHeight(slide1) * 0.5} top`,
            scrub: true,
            invalidateOnRefresh: true,
          },
        }
      );
      }

      // === SLIDE 3 ANIMATION ===
      // Pin section for full viewport height (like PowerPoint)
      ScrollTrigger.create({
        trigger: slide3,
        start: () => "top top",
        end: () => `+=${getSlideHeight(slide3)}`,
        pin: slide3,
        pinSpacing: true,
        scrub: false,
        invalidateOnRefresh: true,
      });

      // Fade in animation when section enters viewport (support scroll up)
      // Only fade content, not background
      if (slide3Content.length > 0) {
        slide3Content.forEach((el) => {
      gsap.fromTo(
            el,
            { opacity: 0, y: 30 },
        {
              opacity: 1,
          y: 0,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: slide3,
                  start: () => "top 80%",
                  end: () => "top 20%",
            scrub: true,
            invalidateOnRefresh: true,
          },
        }
      );
        });
      }

      // Fade out animation when section exits (support scroll up)
      // Only fade content, keep background visible to prevent black screen
      if (slide3Content.length > 0) {
        slide3Content.forEach((el) => {
      gsap.fromTo(
            el,
            { opacity: 1, y: 0 },
        {
              opacity: 0,
          y: -30,
          duration: 1,
          ease: "power2.in",
          scrollTrigger: {
            trigger: slide3,
                  start: () => "bottom top",
                  end: () => `bottom+=${getSlideHeight(slide3) * 0.5} top`,
            scrub: true,
            invalidateOnRefresh: true,
          },
        }
      );
        });
      }

      const handleLoad = () => requestAnimationFrame(() => ScrollTrigger.refresh());
      window.addEventListener("load", handleLoad);

      // Refresh ScrollTrigger saat resize
      const handleResize = () => {
        ScrollTrigger.refresh();
      };
      window.addEventListener("resize", handleResize);

      // Refresh setelah setup
      requestAnimationFrame(() => ScrollTrigger.refresh());

      return () => {
        window.removeEventListener("resize", handleResize);
        window.removeEventListener("load", handleLoad);
      };
    }, formSectionRef);

    return () => ctx.revert();
  }, []);

  // Handle focus when section is scrolled to via hash or Contact Us button
  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash === "#contact-us" || window.location.hash === "#form") {
        // Small delay to ensure scroll is complete
        setTimeout(() => {
          if (nameInputRef.current) {
            nameInputRef.current.focus();
          }
        }, 500);
      }
    };

    // Check on mount if hash is already set
    if (window.location.hash === "#contact-us" || window.location.hash === "#form") {
      handleHashChange();
    }

    // Listen for hash changes
    window.addEventListener("hashchange", handleHashChange);

    // Listen for clicks on Contact Us links
    const handleContactUsClick = (e) => {
      const target = e.target.closest('a[href="#contact-us"], a[href*="#contact-us"]');
      if (target) {
        setTimeout(() => {
          if (nameInputRef.current) {
            nameInputRef.current.focus();
          }
        }, 500);
      }
    };

    document.addEventListener("click", handleContactUsClick);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
      document.removeEventListener("click", handleContactUsClick);
    };
  }, []);

  useEffect(() => {
    const storedDraft = localStorage.getItem("aiDiagnosticDraft");
    if (!storedDraft) return;

    try {
      const data = JSON.parse(storedDraft);
      const rawRecommendations = Array.isArray(data.recommendations) ? data.recommendations : [];
      const formattedRecommendations = rawRecommendations
        .map((rec, index) => {
          if (typeof rec === "string") {
            return `${index + 1}. ${rec}`;
          }
          const recText = rec?.en ? tr(rec.en, rec.id || rec.en) : "";
          return recText ? `${index + 1}. ${recText}` : null;
        })
        .filter(Boolean)
        .join("\n");

      const blockParts = [
        "AI Diagnostic",
        data?.role ? `${tr("Role", "Role")}: ${data.role}` : null,
        data?.description ? `${tr("Problem", "Masalah")}: ${data.description}` : null,
        formattedRecommendations
          ? `${tr("Recommendations", "Rekomendasi")}:\n${formattedRecommendations}`
          : null,
      ].filter(Boolean);

      if (blockParts.length > 0) {
        setFormData((prev) => ({
          ...prev,
          projectDetails: [prev.projectDetails, blockParts.join("\n")]
            .filter(Boolean)
            .join("\n\n"),
        }));
      }
    } catch (error) {
      console.error("Failed to parse AI Diagnostic draft:", error);
    } finally {
      localStorage.removeItem("aiDiagnosticDraft");
    }
  }, [tr]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const toggleSolution = (solution) => {
    setFormData((prev) => {
      const current = Array.isArray(prev.solutions) ? prev.solutions : [];
      const has = current.includes(solution);
      return {
        ...prev,
        solutions: has ? current.filter((s) => s !== solution) : [...current, solution],
      };
    });
  };

  const togglePackage = (pkg) => {
    setFormData((prev) => ({
      ...prev,
      packages: prev.packages === pkg ? "" : pkg,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage("");

    // Validate EmailJS credentials
    if (
      !EMAILJS_SERVICE_ID ||
      !EMAILJS_TEMPLATE_ID ||
      !EMAILJS_PUBLIC_KEY ||
      EMAILJS_SERVICE_ID === "YOUR_SERVICE_ID" ||
      EMAILJS_TEMPLATE_ID === "YOUR_TEMPLATE_ID" ||
      EMAILJS_PUBLIC_KEY === "YOUR_PUBLIC_KEY"
    ) {
      setSubmitStatus("error");
      setErrorMessage(
        "EmailJS belum dikonfigurasi. Silakan setup EmailJS credentials terlebih dahulu."
      );
      setIsSubmitting(false);
      console.error(
        "EmailJS credentials belum di-setup. Silakan baca EMAILJS_SETUP.md untuk instruksi."
      );
      return;
    }

    try {
      const solutionsStr = Array.isArray(formData.solutions) && formData.solutions.length > 0
          ? formData.solutions.join(", ")
          : "None selected";
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone || "Not provided",
        project_details: formData.projectDetails,
        solutions: solutionsStr,
        packages: formData.packages || "None selected",
        to_email: "metagent.studio@gmail.com",
        reply_to: formData.email,
      };

      console.log("Sending email with params:", templateParams);
      console.log("EmailJS Config:", {
        serviceId: EMAILJS_SERVICE_ID,
        templateId: EMAILJS_TEMPLATE_ID,
        publicKey: EMAILJS_PUBLIC_KEY.substring(0, 10) + "...",
      });

      const response = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );

      console.log("Email sent successfully:", response);
      setSubmitStatus("success");
      setFormData({
        name: "",
        email: "",
        phone: "",
        projectDetails: "",
        solutions: [],
        packages: "",
      });
    } catch (error) {
      console.error("Error sending email:", error);
      console.error("Error details:", {
        code: error.code,
        text: error.text,
        status: error.status,
      });
      
      let errorMsg = "Sorry, there was an error sending your request. Please try again.";
      
      if (error.text) {
        errorMsg = `Error: ${error.text}`;
      } else if (error.message) {
        errorMsg = `Error: ${error.message}`;
      }
      
      setErrorMessage(errorMsg);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="form" className="form-section" data-contact-us="true">
      <a id="contact-us" style={{ position: 'absolute', top: 0, visibility: 'hidden' }} aria-hidden="true" />
      
      <form className="contact-form" onSubmit={handleSubmit} ref={formSectionRef}>
        {/* Slide 1: Heading Banner & Personal Information */}
        <FormSlide className="form-slide-heading-personal" slideRef={slide1Ref}>
        <div className="form-heading-banner">
          <div className="heading-content">
            <div className="heading-line">{tr("Let's Make", "Ayo Buat")}</div>
            <div className="heading-line">
              {tr("Something", "Sesuatu")} <span className="fire-emoji">🔥</span>
            </div>
          </div>
        </div>
        <div className="form-group">
          <p className="form-text">
            {tr("Hello! My name is", "Halo! Namaku")}{" "}
            <input
              ref={nameInputRef}
              type="text"
              name="name"
              className="form-input inline-input"
              placeholder={tr("your full name", "nama lengkap")}
              value={formData.name}
              onChange={handleInputChange}
              required
            />
            {" "}
            {tr(
              "and I'm looking to integrate intelligent agents to automate and scale my operations.",
              "dan aku ingin mengintegrasikan intelligent agents untuk mengotomasi dan menskalakan operasional."
            )}
          </p>
          <p className="form-text">
            {tr("You can email me at", "Kamu bisa email aku di")}{" "}
            <input
              type="email"
              name="email"
              className="form-input inline-input"
              placeholder={tr("your@email.com", "email@kamu.com")}
              value={formData.email}
              onChange={handleInputChange}
              required
            />
            {" "}
            {tr("or reach me on", "atau hubungi aku di")}{" "}
            <input
              type="tel"
              name="phone"
              className="form-input inline-input"
              placeholder={tr("your phone #", "nomor telepon")}
              value={formData.phone}
              onChange={handleInputChange}
            />
          </p>
        </div>
        </FormSlide>

        {/* Slide 3: Project Details, Solution Interest & Submit Button */}
        <FormSlide className="form-slide-project-solution-submit" slideRef={slide3Ref}>
        <div className="form-group">
          <h3 className="form-heading">
            {tr("I'm interested in solutions for (select all that apply):", "Saya tertarik dengan solusi untuk (pilih semua yang sesuai):")}
          </h3>
          <div className="solution-buttons">
            {solutionOptions.map((solution) => (
              <button
                key={solution.value}
                type="button"
                className={`solution-button ${
                  (Array.isArray(formData.solutions) && formData.solutions.includes(solution.value)) ? "active" : ""
                }`}
                onClick={() => toggleSolution(solution.value)}
              >
                {solution.label}
              </button>
            ))}
          </div>
        </div>
        <div className="form-group">
          <h3 className="form-heading">
            {tr("I'm interested in which package (select one):", "Saya tertarik dengan paket yang mana (pilih satu):")}
          </h3>
          <div className="solution-buttons">
            {packageOptions.map((pkg) => (
              <button
                key={pkg.value}
                type="button"
                className={`solution-button ${
                  formData.packages === pkg.value ? "active" : ""
                }`}
                onClick={() => togglePackage(pkg.value)}
              >
                {pkg.label}
              </button>
            ))}
          </div>
        </div>
        <div className="form-group">
          <h3 className="form-heading">
            {tr("Here are some details about my project:", "Berikut beberapa detail tentang proyek saya:")}
          </h3>
          <textarea
            name="projectDetails"
            className="form-textarea"
            placeholder={tr("My project is about..", "Proyek saya tentang..")}
            value={formData.projectDetails}
            onChange={handleInputChange}
              rows="2"
            required
          />
        </div>
        <div className="form-submit-container">
          <button
            type="submit"
            className="submit-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? tr("Sending...", "Mengirim...") : tr("Send Request", "Kirim Permintaan")}
          </button>
          {submitStatus === "success" && (
            <p className="submit-message success">
              {tr("Thank you! Your request has been sent successfully.", "Terima kasih! Permintaanmu sudah terkirim.")}
            </p>
          )}
          {submitStatus === "error" && (
            <p className="submit-message error">
              {errorMessage || "Sorry, there was an error sending your request. Please try again."}
            </p>
          )}
        </div>
        </FormSlide>
      </form>
    </section>
  );
}

